import React from "react";
import {IGamesListRenderer} from "./games-list-renderer.interface";
import {IGamesListViewModel} from "./games-list-view-model.interface";
import {IArenaToGamesListAdapter} from "../arenas/arena-to-games-list-adapter.interface";
import {IServiceFactoryExtended} from "../../../service-factory-extended.interface";
import {computed} from "mobx";
import {TextTranslation} from "../../../translation/text-translation";

export abstract class GamesListBaseModel implements IGamesListRenderer, IGamesListViewModel {
    constructor(protected readonly arena: IArenaToGamesListAdapter) {

    }

    abstract get listId(): number;
    abstract get title(): TextTranslation;
    abstract get forceInLayout(): boolean;
    abstract get templateType(): string;

    protected abstract get _isAISorted(): boolean;
    protected abstract get _numberOfGamesToBeIgnoredInAISorting(): number | undefined;
    protected abstract _shouldBeDisplayed(): boolean;
    protected abstract _renderList(listPosition: number): React.ReactElement | null;
    protected abstract _getGameTypes(): number[];
    abstract renderListHeader(): React.ReactElement;

    protected get services(): IServiceFactoryExtended {
        return this.arena.services;
    }

    get arenaName(): string {
        return this.arena.arenaName;
    }

    get listKey(): string {
        return `${this.arena.arenaName}_${this.title.translationKey}_${this.listId}`;
    }

    get aiEngine(): string {
        if(this.arena.aiEngine) {
            return this.arena.aiEngine;
        }

        if(this._isAISorted) {
            return this.services.aiEngine.getAiEngine();
        }

        return "";
    }
    get maxGamesToShowInList(): number {
        return this.arena.maxGamesToShowInList;
    }

    @computed
    get isVisible(): boolean {
        return this._shouldBeDisplayed();
    }

    @computed
    get gameTypes(): number[] {
        return this._applyGameTypesTransformations(this._getGameTypes());
    }

    protected _applyGameTypesTransformations(gameTypes: number[]): number[] {
        return this._reduceGameTypes(
                    this._sortGameTypes(
                        this._distinctGameTypes(gameTypes)));
    }

    protected _sortGameTypes(gameTypes: number[]): number[] {
        if(this._isAISorted) {
            return this.services.aiEngine.sortGames(gameTypes, {ignoreNumberOfGames: this._numberOfGamesToBeIgnoredInAISorting});
        }
        return gameTypes;
    }

    protected _reduceGameTypes(gameTypes: number[]): number[] {
        return gameTypes.filter(gt => this.services.gamesFilter.filteredGamesMap[gt])
                        .slice(0, this.maxGamesToShowInList);
    }

    protected _distinctGameTypes(gameTypes: number[]): number[] {
        return [...new Set(gameTypes)];
    }

    render(listPosition: number): React.ReactElement | null {
        return this._renderList(listPosition);
    }

}